'use client'

import {
    clearStripeCheckoutReturnParams,
    getStripeCheckoutReturnSessionId
} from '@tebuto/payment/stripe/stripe-checkout-return'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { BookEventFormValues } from '../BookingEvent'
import type { EventLocationSelection, ParsedBookableEvent } from '../types'
import { PaymentType } from '../types'
import { clearPendingCheckout } from './pending-checkout-storage'

const REDIRECT_BOOKING_FAILED_MESSAGE = 'Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.'

export type WidgetStripeCheckoutReturnPhase = 'idle' | 'confirming' | 'error'

type BookEventFn = (
    options: {
        event: ParsedBookableEvent
        selectedLocation: EventLocationSelection
    } & Record<string, unknown>
) => Promise<unknown>

type UseWidgetStripeCheckoutReturnOptions = {
    enabled: boolean
    bookingEvent: ParsedBookableEvent | undefined
    bookingData: BookEventFormValues | null | undefined
    bookEvent: BookEventFn
    onBookingSuccess: (booking: unknown) => void
    onBookingFailed: () => void
}

const bookInFlight = new Map<string, Promise<unknown>>()

function clearReturnSessionIdFromUrl(): void {
    if (typeof window === 'undefined') {
        return
    }

    const nextPath = clearStripeCheckoutReturnParams(
        window.location.pathname,
        new URLSearchParams(window.location.search)
    )
    window.history.replaceState(window.history.state, '', nextPath)
}

export function useWidgetStripeCheckoutReturn({
    enabled,
    bookingEvent,
    bookingData,
    bookEvent,
    onBookingSuccess,
    onBookingFailed
}: UseWidgetStripeCheckoutReturnOptions): {
    phase: WidgetStripeCheckoutReturnPhase
    errorMessage: string | null
    returnSessionId: string | null
    resetReturnError: () => void
} {
    const returnSessionId =
        enabled && typeof window !== 'undefined'
            ? getStripeCheckoutReturnSessionId(new URLSearchParams(window.location.search))
            : null

    const [phase, setPhase] = useState<WidgetStripeCheckoutReturnPhase>(() => (returnSessionId ? 'confirming' : 'idle'))
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const handledSessionIdRef = useRef<string | null>(null)

    const onBookingSuccessRef = useRef(onBookingSuccess)
    const onBookingFailedRef = useRef(onBookingFailed)
    const bookEventRef = useRef(bookEvent)
    onBookingSuccessRef.current = onBookingSuccess
    onBookingFailedRef.current = onBookingFailed
    bookEventRef.current = bookEvent

    const completeRedirectBooking = useCallback(
        async (sessionId: string): Promise<unknown> => {
            if (!bookingEvent || !bookingData) {
                throw new Error('Pending checkout state is missing')
            }

            return bookEventRef.current({
                event: bookingEvent,
                ...bookingData,
                selectedLocation: bookingData.selectedLocation as unknown as EventLocationSelection,
                paymentType: PaymentType.ONLINE,
                orderId: sessionId
            })
        },
        [bookingData, bookingEvent]
    )

    const resetReturnError = useCallback(() => {
        setPhase('idle')
        setErrorMessage(null)
        handledSessionIdRef.current = null
    }, [])

    useEffect(() => {
        if (!enabled || !returnSessionId || !bookingEvent || !bookingData) {
            return
        }

        const sessionId = returnSessionId

        if (handledSessionIdRef.current === sessionId) {
            return
        }

        let cancelled = false
        setPhase('confirming')
        setErrorMessage(null)

        async function processReturnedCheckoutSession(): Promise<void> {
            let inFlight = bookInFlight.get(sessionId)
            if (!inFlight) {
                inFlight = completeRedirectBooking(sessionId).finally(() => {
                    bookInFlight.delete(sessionId)
                })
                bookInFlight.set(sessionId, inFlight)
            }

            try {
                const booking = await inFlight
                if (cancelled) {
                    return
                }

                handledSessionIdRef.current = sessionId
                clearReturnSessionIdFromUrl()
                clearPendingCheckout()
                setPhase('idle')
                onBookingSuccessRef.current(booking)
            } catch {
                if (cancelled) {
                    return
                }

                handledSessionIdRef.current = sessionId
                clearReturnSessionIdFromUrl()

                setPhase('error')
                setErrorMessage(REDIRECT_BOOKING_FAILED_MESSAGE)
                onBookingFailedRef.current()
            }
        }

        void processReturnedCheckoutSession()

        return () => {
            cancelled = true
        }
    }, [bookingData, bookingEvent, completeRedirectBooking, enabled, returnSessionId])

    return { phase, errorMessage, returnSessionId, resetReturnError }
}
